import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tooltip } from '@radix-ui/themes';
import { AlertCircle, Check, Clock, RotateCw } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { TONE_CLASSES } from 'components/admin/audit/pillTone';
import ProgressBar from './ProgressBar';
import { journalLink } from 'api/sectorAlignmentApi';
import type { DeskAnalysis, DeskSource, LastRun } from 'api/sectorAlignmentApi';
import { sourceLabel } from './deskSelection';
import { isStale, timeOf } from './staleness';

interface DeskCardProps {
  name: string;
  source: DeskSource | null;
  desk?: DeskAnalysis;
  analyzing: boolean;
  /** AC12: a relaunch keeps the result on screen with an indicator, realign disabled. */
  refreshing?: boolean;
  /** This desk's running job; total falls back to the analysis while the job waits (AC14). */
  progress: { done: number; total: number } | null;
  busyElsewhere: boolean;
  /** The last run, only when it was on this desk. */
  lastRun: LastRun;
  justFinished: boolean;
  now: number;
  onRetry: () => void;
  onRealign: () => void;
}

type InsertProps = { tone: 'amber' | 'emerald'; title: string; action: ReactNode } | null;

export const SPINNER = 'h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900 dark:border-zinc-700 dark:border-t-zinc-50';

/** One desk: skeleton (3a), running job (7b), error (9a/B8), pending (B5), aligned (3b/7e/7h), or counters (3b/7f/7g). */
const DeskCard = (props: DeskCardProps) => {
  const { name, source, desk, analyzing, refreshing = false, progress, busyElsewhere, lastRun, justFinished, now, onRetry, onRealign } = props;
  const { t } = useTranslation();
  const globeData = source === 'globe-data';
  // Radix tooltips do not fire on a disabled button: the span carries it.
  const oneAtATime = (button: ReactNode) =>
    busyElsewhere ? (
      <Tooltip content={t('One realignment at a time.')}>
        <span tabIndex={0} className="self-start">{button}</span>
      </Tooltip>
    ) : (
      button
    );
  const relaunch = oneAtATime(
    <Button variant="outline" size="sm" onClick={onRealign} disabled={busyElsewhere || refreshing}>
      <RotateCw aria-hidden="true" />
      {t('Relaunch')}
    </Button>
  );
  const failures = lastRun?.failures ?? [];
  const failureLines = (
    <>
      {failures.map((f) => (
        <p key={f.key} className="text-[12.5px] text-muted-foreground">
          <span className="font-mono text-foreground">{f.key}</span> · {f.reason || '—'}
        </p>
      ))}
      {(lastRun?.failed ?? 0) > failures.length && (
        <p className="text-[12.5px] text-muted-foreground">{t('{{count}} more', { count: (lastRun?.failed ?? 0) - failures.length })}</p>
      )}
    </>
  );

  let body: ReactNode;
  let insert: InsertProps = null;
  if (analyzing) {
    body = (
      <>
        <Skeleton className="h-[30px] w-[120px] rounded-lg dark:bg-zinc-800" />
        <Skeleton className="h-3 w-[220px] dark:bg-zinc-800" />
        <Skeleton className="h-3 w-[160px] dark:bg-zinc-800" />
        <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
          <span className={SPINNER} aria-hidden="true" />
          {globeData ? t('Reading stores in Globe Data…') : t('Reading stores in Jira…')}
        </p>
      </>
    );
  } else if (progress) {
    body = (
      <>
        <ProgressBar done={progress.done} total={progress.total || desk?.misaligned || 0} />
        <Button variant="outline" disabled className="self-start">
          {t('Realignment in progress…')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('You can leave the page: the operation continues.')}</p>
      </>
    );
  } else if (!desk) {
    body = (
      <p className="flex items-center gap-2.5 text-sm font-medium text-muted-foreground">
        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-dashed border-zinc-300 dark:border-zinc-600">
          <Clock className="h-[15px] w-[15px]" aria-hidden="true" />
        </span>
        {t('Waiting for analysis')}
      </p>
    );
  } else if (desk.status === 'error') {
    body = (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-[11px] border bg-background px-4 py-3.5">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]', TONE_CLASSES.rose)}>
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="min-w-[200px] flex-1 text-[13.5px] font-medium">{globeData
            ? t('Globe Data is not responding for {{desk}}. Nothing was changed.', { desk: name })
            : t('Jira is not responding for {{desk}}. Nothing was changed.', { desk: name })}</span>
        <Button variant="outline" className="shrink-0" onClick={onRetry}>
          <RotateCw aria-hidden="true" />
          {t('Retry')}
        </Button>
      </div>
    );
  } else if (desk.misaligned <= 0) {
    if (lastRun && !justFinished) {
      insert = {
        tone: 'emerald',
        title: t('{{desk}} realignment finished at {{time}}: {{count}} tickets realigned.', { desk: name, time: timeOf(lastRun.at), count: lastRun.applied }),
        action: <Link to={journalLink(lastRun.realignmentId)} className="text-[12.5px] text-muted-foreground underline underline-offset-[3px]">{t('View in the journal')}</Link>,
      };
    }
    body = (
      <>
        <p className="flex items-center gap-2.5 text-[15px] font-semibold">
          <span className={cn('flex h-[30px] w-[30px] items-center justify-center rounded-full', TONE_CLASSES.emerald)}>
            <Check className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
          </span>
          {t('Everything is aligned')}
        </p>
        {justFinished && <p className="text-xs text-muted-foreground">{t('The BI dashboard will be up to date after the nightly sync.')}</p>}
      </>
    );
  } else {
    // R22: a partial/interrupted run is flagged on the card for an hour, the journal keeps it after.
    const outcome = lastRun && !isStale(lastRun.at, null, now) ? lastRun.outcome : 'done';
    if (lastRun && outcome === 'partial') {
      insert = {
        tone: 'amber',
        title: `${t('{{count}} tickets realigned', { count: lastRun.applied })} · ${t('{{count}} failures', { count: lastRun.failed })}`,
        action: <>{failureLines}{relaunch}</>,
      };
    } else if (lastRun && outcome === 'interrupted') {
      insert = {
        tone: 'amber',
        title: t('Realignment interrupted after {{count}} tickets. The {{applied}} realigned tickets stay realigned.', {
          count: lastRun.total,
          applied: lastRun.applied,
        }),
        action: <>{failureLines}{relaunch}</>,
      };
    }
    body = (
      <>
        <div>
          <p className="flex items-baseline gap-2">
            <span className="text-[30px] font-semibold tabular-nums">{desk.misaligned}</span>
            <span className="text-sm font-medium">{t('misaligned tickets', { count: desk.misaligned })}</span>
          </p>
          {desk.closed > 0 && (
            <p className="text-[12.5px] text-muted-foreground">{t('including {{count}} closed, also affected', { count: desk.closed })}</p>
          )}
          <p className="text-[12.5px] text-muted-foreground">{t('{{count}} stores affected', { count: desk.stores?.length ?? 0 })}</p>
        </div>
        {!insert &&
          oneAtATime(
            <Button variant="outline" className="self-start" onClick={onRealign} disabled={busyElsewhere || refreshing}>
              {t('Realign {{count}} tickets', { count: desk.misaligned })}
            </Button>
          )}
      </>
    );
  }

  return (
    <section
      aria-label={name}
      aria-busy={analyzing || refreshing}
      className="flex flex-col gap-3 rounded-[13px] border bg-card px-5 pb-5 pt-[18px] shadow-[0_1px_2px_rgba(16,24,40,.04)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-[14.5px] font-semibold">{name}</h2>
          {source && <p className="text-xs text-muted-foreground">{sourceLabel(t, source)}</p>}
        </div>
        {refreshing && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={SPINNER} aria-hidden="true" />
            {t('Analysis in progress…')}
          </span>
        )}
      </div>
      {body}
      {insert && (
        <div className="flex flex-col gap-2 rounded-[10px] border bg-background px-3.5 py-3">
          <p className="flex items-center gap-2 text-[13.5px] font-medium">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', insert.tone === 'amber' ? 'bg-amber-500' : 'bg-emerald-500')} aria-hidden="true" />
            {insert.title}
          </p>
          <div className="flex flex-col items-start gap-1.5 pl-[17px]">{insert.action}</div>
        </div>
      )}
    </section>
  );
};

export default DeskCard;
